import React, { Dispatch, SetStateAction, useState } from "react"
import {
  CartContainer,
  CartItems,
  Label
} from "../../Order/ManageOrder/DeliverOrder/Cart/Cart.styled"
import * as graphql from "lib/types/generated/graphql-types"
import {
  CustomReasonCode,
  Maybe,
  Order,
  OrderHistory,
  OrderOperationSearchHit,
  OrderModificationAddresses,
  OrderAddressesModificationSearchHit,
  OrderOperationAddressModificationUpdateAddress
} from "lib/types/generated/graphql-types"
import Reason from "../../Order/Updates/Reason"
import { ArrowIcon, OrderLabel } from "../Shared.styled"
import { Link } from "react-router-dom"
import StartOperation from "./StartOperation"
import { useMutation } from "@apollo/client"
import START_ADDRESS_MODIFICATION from "../../../graphql/mutations/order/modification/address/StartOrderModificationAddresses"
import { handleErrorMessages } from "helpers/errors"
import ErrorMessages from "../../Order/ManageOrder/ErrorMessages"
import CopyButton from "../../Ui/CopyButton"
import {
  Container,
  Title,
  ProviderWrapper,
  Provider,
  Providers,
  ActionWrapper,
  LabelWrapper,
} from "../../Order/ManageOrder/DeliverOrder/FailedDeliveries.styled"
import ActionInfoBox from "./ActionInfoBox"
import { ActionDropdownButton } from "../../Ui/Buttons/ActionDropdownButton"
import { ReactComponent as PaymentIcon } from "../../../images/icons/credit-card.svg"
import {
  AddressComparison,
  AddressSection,
  AddressSectionTitle,
  AddressGridHeader,
  AddressRow,
  FieldLabel,
  FieldValue
} from "./AddressModificationAction.styled"

type Props = {
  operation: OrderOperationSearchHit
  order: Order
  orderHistory: OrderHistory
  getCustomCode: (code: Maybe<string> | undefined) => CustomReasonCode
  setShow: Dispatch<SetStateAction<boolean>>
  setCreatedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
}

const ADDRESS_FIELDS: { key: keyof OrderOperationAddressModificationUpdateAddress; label: string }[] = [
  { key: "givenName", label: "Given name" },
  { key: "familyName", label: "Family name" },
  { key: "streetAddress", label: "Street address" },
  { key: "streetAddress2", label: "Street address 2" },
  { key: "postalCode", label: "Postal code" },
  { key: "city", label: "City" },
  { key: "stateOrProvince", label: "State/Province" },
  { key: "country", label: "Country" },
  { key: "email", label: "Email" },
  { key: "telephoneNumber", label: "Phone" }
]

const AddressModificationAction = ({
  operation,
  order,
  orderHistory,
  getCustomCode,
  setShow,
  setCreatedOperations
}: Props) => {
  const [paymentAction, setPaymentAction] =
    useState<graphql.InputActionPaymentOrderModificationShippingFees>({
      actionType: graphql.InputActionType.Auto
    })
  const [giftCardAction, setGiftCardAction] = useState<graphql.InputActionGiftCard>({
    actionType: graphql.InputActionType.Auto
  })
  const [bonusAction, setBonusAction] = useState<graphql.InputActionBonus>({
    actionType: graphql.InputActionType.Auto
  })

  const searchOperation = operation as OrderAddressesModificationSearchHit
  const searchAddresses = searchOperation.addresses

  const modification = order?.Modifications?.find(
    (mod): mod is OrderModificationAddresses =>
      mod.__typename === "OrderModificationAddresses" && mod.id === operation.id
  )

  const [startAddressModification, { loading }] = useMutation(
    START_ADDRESS_MODIFICATION,
    {
      onCompleted: () => {
        setShow(false)
        setCreatedOperations((prev) => prev?.filter((hit) => hit.id !== operation?.id))
      },
      onError: (error) => {
        handleErrorMessages(error)
      }
    }
  )

  const startOperation = () => {
    startAddressModification({
      variables: {
        modificationId: operation.id,
        input: JSON.stringify({
          payment: paymentAction
        })
      }
    })
  }

  const isModificationFailed = modification?.failed && !modification.completed
  const hasPaymentProvider = !!modification?.paymentProvider

  const renderAddressFields = (
    prev: OrderOperationAddressModificationUpdateAddress | null | undefined,
    current: OrderOperationAddressModificationUpdateAddress | null | undefined
  ) => {
    return ADDRESS_FIELDS.filter(({ key }) => {
      const prevValue = prev?.[key] ?? "—"
      const currentValue = current?.[key] ?? "—"
      return prevValue !== currentValue
    }).map(({ key, label }) => {
      const prevValue = prev?.[key] ?? "—"
      const currentValue = current?.[key] ?? "—"

      return (
        <AddressRow key={key}>
          <FieldLabel>{label}</FieldLabel>
          <FieldValue>{prevValue}</FieldValue>
          <FieldValue>{currentValue}</FieldValue>
        </AddressRow>
      )
    })
  }

  const hasChangedFields = (
    prev: OrderOperationAddressModificationUpdateAddress | null | undefined,
    current: OrderOperationAddressModificationUpdateAddress | null | undefined
  ) => ADDRESS_FIELDS.some(({ key }) => (prev?.[key] ?? "—") !== (current?.[key] ?? "—"))

  const renderAddressComparison = () => {
    const prevData = searchAddresses?.prev
    const currentData = searchAddresses?.data

    if (!prevData && !currentData) return null

    const showShipping = hasChangedFields(prevData?.shippingAddress, currentData?.shippingAddress)
    const showBilling = hasChangedFields(prevData?.billingAddress, currentData?.billingAddress)

    if (!showShipping && !showBilling) return null

    return (
      <AddressComparison>
        {showShipping && (
          <AddressSection>
            <AddressSectionTitle>Shipping Address</AddressSectionTitle>
            <AddressGridHeader>
              <span>Field</span>
              <span>Before</span>
              <span>After</span>
            </AddressGridHeader>
            {renderAddressFields(prevData?.shippingAddress, currentData?.shippingAddress)}
          </AddressSection>
        )}

        {showBilling && (
          <AddressSection>
            <AddressSectionTitle>Billing Address</AddressSectionTitle>
            <AddressGridHeader>
              <span>Field</span>
              <span>Before</span>
              <span>After</span>
            </AddressGridHeader>
            {renderAddressFields(prevData?.billingAddress, currentData?.billingAddress)}
          </AddressSection>
        )}
      </AddressComparison>
    )
  }

  const capitalize = (s: string) => s && s[0].toUpperCase() + s.slice(1)
  const getLabel = (value: string) => capitalize(value.toLowerCase())

  if (!order) {
    return null
  }

  if (isModificationFailed) {
    return (
      <>
        <OrderLabel>
          Order: {order.reference}{" "}
          <Link to={`/orders/${order.id}`} target="_blank">
            <ArrowIcon />
          </Link>
        </OrderLabel>
        <Container>
          <Title>
            <div>
              <h2>Failed Modification addresses</h2>
            </div>
          </Title>
          <span>id: {modification.id} <CopyButton string={modification.id} /></span>
          {hasPaymentProvider && (
            <ProviderWrapper>
              <h4>Failed providers:</h4>
              <Provider>Payment</Provider>
            </ProviderWrapper>
          )}
          <CartItems>
            <Label style={{ marginTop: "16px" }}>Addresses</Label>
            {renderAddressComparison()}

            <Reason reason={modification?.reason} getCustomCode={getCustomCode} />
          </CartItems>

          <ActionInfoBox showManual={false} />

          <Providers>
            {hasPaymentProvider && (
              <div>
                <ActionWrapper>
                  <LabelWrapper>
                    <PaymentIcon />
                    <b>Payment - {modification.paymentProvider?.providerName}</b>
                  </LabelWrapper>
                  <ActionDropdownButton title={getLabel(paymentAction.actionType)}>
                    <li
                      onClick={() =>
                        setPaymentAction({ actionType: graphql.InputActionType.Auto })
                      }
                    >
                      Auto
                    </li>
                    <li
                      onClick={() =>
                        setPaymentAction({ actionType: graphql.InputActionType.Manual })
                      }
                    >
                      Manual
                    </li>
                    <li
                      onClick={() =>
                        setPaymentAction({ actionType: graphql.InputActionType.Skip })
                      }
                    >
                      Skip
                    </li>
                  </ActionDropdownButton>
                </ActionWrapper>
              </div>
            )}
          </Providers>

          <ErrorMessages
            orderHistory={orderHistory.history}
            operationId={modification.id}
            operation={"ADDRESS_MODIFICATION"}
          />
        </Container>
      </>
    )
  }

  return (
    <>
      <CartContainer>
        <OrderLabel>
          Order: {order.reference}{" "}
          <Link to={`/orders/manage-order/${order.id}`} target="_blank">
            <ArrowIcon />
          </Link>
        </OrderLabel>
        <CartItems>
          <Label style={{ marginTop: "16px" }}>Addresses</Label>
          {renderAddressComparison()}

          <Reason reason={modification?.reason} getCustomCode={getCustomCode} />
        </CartItems>
        <StartOperation
          operation={modification}
          actionState={{
            giftCardAction,
            setGiftCardAction,
            bonusAction,
            setBonusAction,
            paymentAction,
            setPaymentAction
          }}
          startOperation={startOperation}
          loading={loading}
        />
      </CartContainer>
    </>
  )
}

export default AddressModificationAction
